import { getGatewayConfig } from '@/lib/env-config';
import { supabase } from './supabase';

/**
 * Result type compatible with CloudFunctionCallResult
 */
export interface GatewayCallResult {
  success: boolean;
  data?: unknown;
  error?: string;
  statusCode?: number;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Get Supabase JWT access token from current session
 */
async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

/**
 * Core HTTP request helper with:
 * - Bearer JWT auth
 * - 401 retry (refresh session once and retry)
 * - 5xx retry (up to 2 retries with exponential backoff)
 */
async function request(
  method: HttpMethod,
  path: string,
  body?: unknown,
  timeoutMs: number = 30000,
  retries: number = 2
): Promise<GatewayCallResult> {
  const gatewayConfig = getGatewayConfig();
  if (!gatewayConfig.baseUrl) {
    console.error('❌ Gateway API base URL not configured');
    return {
      success: false,
      error: 'Gateway API base URL not configured in environment',
    };
  }

  let authToken = await getAuthToken();
  if (!authToken) {
    console.error('❌ No valid Supabase session for gateway request');
    return {
      success: false,
      error: 'User is not authenticated',
    };
  }

  const url = `${gatewayConfig.baseUrl}${path}`;
  const maxRetries = retries;
  const initialDelay = 1000;

  console.log(`🌐 Gateway request: ${method} ${url}`, {
    hasBody: body !== undefined,
    body: body,
    hasAuthToken: !!authToken,
    tokenPrefix: authToken ? authToken.substring(0, 20) + '...' : 'none',
  });

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt > 0) {
      const delay = initialDelay * Math.pow(2, attempt - 1);
      console.log(`🔄 Gateway retry attempt ${attempt}/${maxRetries} after ${delay}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${authToken}`,
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        mode: 'cors',
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(timeoutMs),
      });
    } catch (err) {
      console.error(`❌ Gateway fetch error (attempt ${attempt}):`, err);
      if (attempt < maxRetries) continue;
      return {
        success: false,
        error: `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`,
      };
    }

    console.log(`📡 Gateway response: ${method} ${path}`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      attempt,
    });

    // 401 — try refreshing the token once
    if (response.status === 401 && attempt === 0) {
      console.warn('⚠️ Gateway returned 401, refreshing Supabase session...');
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        authToken = session.access_token;
        // Retry immediately (don't count as a retry attempt for 5xx)
        const retryHeaders: Record<string, string> = {
          Authorization: `Bearer ${authToken}`,
        };
        if (body !== undefined) {
          retryHeaders['Content-Type'] = 'application/json';
        }

        try {
          response = await fetch(url, {
            method,
            mode: 'cors',
            headers: retryHeaders,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal: AbortSignal.timeout(timeoutMs),
          });
        } catch (err) {
          return {
            success: false,
            error: `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`,
          };
        }

        if (response.status === 401) {
          return {
            success: false,
            error: 'Authentication failed. Please try again or contact support.',
            statusCode: 401,
          };
        }
      } else {
        return {
          success: false,
          error: 'Authentication failed. Session expired.',
          statusCode: 401,
        };
      }
    }

    // 5xx — retry with backoff
    if (response.status >= 500 && attempt < maxRetries) {
      console.warn(
        `⚠️ Gateway returned ${response.status}, retrying (attempt ${attempt + 1}/${maxRetries})...`
      );
      continue;
    }

    // 4xx (non-401) or final attempt failure
    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `❌ Gateway error - ${method} ${path} - Status: ${response.status}, Response: ${errorText}`
      );
      return {
        success: false,
        error: `Gateway error: ${response.status} - ${errorText}`,
        statusCode: response.status,
      };
    }

    // Success — parse response
    const contentType = response.headers.get('content-type');
    let data: unknown;
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    console.log(`✅ Gateway success: ${method} ${path}`, {
      statusCode: response.status,
      contentType,
      data,
    });

    return {
      success: true,
      data,
      statusCode: response.status,
    };
  }

  // Should not be reached, but safety fallback
  return {
    success: false,
    error: 'Max retries exceeded',
  };
}

/**
 * Normalize job details response: merge top-level audioFileUrl/transcriptFileUrl
 * into the job object for backward compatibility with existing consumers.
 */
function normalizeJobDetailsResponse(data: unknown): unknown {
  if (data && typeof data === 'object') {
    const response = data as Record<string, unknown>;
    const job = response.job as Record<string, unknown> | undefined;
    if (job) {
      if (response.audioFileUrl !== undefined) {
        job.audioFileUrl = response.audioFileUrl;
      }
      if (response.transcriptFileUrl !== undefined) {
        job.transcriptFileUrl = response.transcriptFileUrl;
      }
    }
  }
  return data;
}

// ─── Gateway Service ───────────────────────────────────────────────────────────

export const gatewayService = {
  /**
   * Complete recording and start AI processing
   * POST /api/scribe/jobs/{scribeJobId}/complete-recording
   */
  async completeRecording(scribeJobId: string): Promise<GatewayCallResult> {
    return request(
      'POST',
      `/api/scribe/jobs/${encodeURIComponent(scribeJobId)}/complete-recording`,
      undefined,
      120000,
      0
    );
  },

  /**
   * Get job details by scribe job ID
   * GET /api/scribe/jobs/{scribeJobId}
   */
  async getJobDetailsByJobId(
    scribeJobId: string
  ): Promise<GatewayCallResult> {
    const result = await request(
      'GET',
      `/api/scribe/jobs/${encodeURIComponent(scribeJobId)}`
    );
    if (result.success && result.data) {
      result.data = normalizeJobDetailsResponse(result.data);
    }
    return result;
  },

  /**
   * Get job details by appointment + location + patient
   * GET /api/scribe/jobs?appointmentId=&locationId=&patientId=
   */
  async getJobDetailsByAppointmentInfo(
    appointmentId: string,
    locationId: string,
    patientId: string
  ): Promise<GatewayCallResult> {
    const params = new URLSearchParams({
      appointmentId,
      locationId,
      patientId,
    });
    const result = await request('GET', `/api/scribe/jobs?${params}`);
    if (result.success && result.data) {
      result.data = normalizeJobDetailsResponse(result.data);
    }
    return result;
  },

  /**
   * Get completed scribe jobs with optional filters
   * GET /api/scribe/jobs/completed?limit=&offset=&...
   */
  async getCompletedScribeJobs(
    params: Record<string, string | number | undefined> = {}
  ): Promise<GatewayCallResult> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const qs = searchParams.toString();
    return request(
      'GET',
      `/api/scribe/jobs/completed${qs ? `?${qs}` : ''}`
    );
  },

  /**
   * Get signed URL for a GCS file key via gateway
   * POST /api/scribe/signed-url
   * Used for files stored in Google Cloud Storage (global recording flow)
   */
  async getSignedUrl(fileKey: string): Promise<GatewayCallResult> {
    return request('POST', '/api/scribe/signed-url', { fileKey });
  },

  /**
   * Update scribe job status
   * PATCH /api/scribe/jobs/{scribeJobId}/status
   */
  async updateScribeJobStatus(
    scribeJobId: string,
    status: string
  ): Promise<GatewayCallResult> {
    return request(
      'PATCH',
      `/api/scribe/jobs/${encodeURIComponent(scribeJobId)}/status`,
      { status }
    );
  },

  /**
   * Update a scribe section
   * PUT /api/scribe/sections/{sectionId}
   */
  async updateSection(
    sectionId: string,
    modifiedJson: unknown
  ): Promise<GatewayCallResult> {
    return request(
      'PUT',
      `/api/scribe/sections/${encodeURIComponent(sectionId)}`,
      { modifiedJson }
    );
  },

  /**
   * Authenticate with an integration partner's external auth API.
   * POST /api/integration/authenticate (Bearer JWT required)
   * Gateway fetches partner credentials from Secret Manager, calls partner's auth URL.
   * Returns the partner's raw response (typically {token, expiresIn}).
   */
  async authenticateIntegration(params: {
    secretId: string;
    username: string;
    url: string;
  }): Promise<GatewayCallResult> {
    return request('POST', '/api/integration/authenticate', params);
  },

  /**
   * Create a scribe job
   * POST /api/scribe/jobs
   * createdBy is auto-filled from JWT on the server
   */
  async createScribeJob(params: {
    appointmentId: string;
    locationId: string;
    patientId: string;
    firstName: string;
    lastName: string;
    dob: string;
    gender: string;
    service: string;
    appointmentStartTime: string;
  }): Promise<GatewayCallResult> {
    return request('POST', '/api/scribe/jobs', params);
  },

  /**
   * Make a call to an integration partner endpoint
   * POST /api/integration/call
   */
  async callIntegrationPartner(params: {
    partnerUrl: string;
    methodType: string;
    token: string;
    locationId: string;
    appointmentId: string;
    jsonString: string;
  }): Promise<GatewayCallResult> {
    return request('POST', '/api/integration/call', params);
  },
};
